import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect } from "react";
import { ArrowUpRight, Phone, MapPin, Star } from "lucide-react";
import Marquee from "@/components/Marquee";
import { RevealText, FadeUp, ClipImage } from "@/components/Reveal";
import { PageHead } from "@/components/utils";
import { BUSINESS, IMAGES, FEATURED_SERVICES, CHAPTERS, FAQS, GALLERY } from "@/lib/data";
import { IDS } from "@/lib/testIds";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const HERO_LINES = ["Precision cuts.", "Editorial colour.", "Bridal, considered."];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#1B1B1B] text-[#F3EAD9]"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Interior of Good Looks Salon"
          className="w-full h-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1B1B1B]/40 via-[#1B1B1B]/30 to-[#1B1B1B]" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 pt-40 md:pt-44 pb-16">
        <div className="flex items-center gap-4 mb-8">
          <motion.span
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.77, 0, 0.175, 1] }}
            className="block h-px w-16 bg-[#C89B3C] origin-left"
          />
          <span className="eyebrow text-white/70">
            Est. Mansa · Punjab
          </span>
        </div>

        <h1
          data-testid={IDS.heroTitle}
          className="font-display leading-[0.92] tracking-tight text-[13vw] md:text-[10vw] lg:text-[8.5vw]"
        >
          {HERO_LINES.map((line, i) => (
            <span key={i} className="block reveal-mask">
              <motion.span
                className="reveal-line inline-block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, ease: [0.77, 0, 0.175, 1], delay: 0.4 + i * 0.12 }}
              >
                {line.split(" ").map((w, j) =>
                  w === "considered." ? (
                    <span key={j} className="italic text-[#C89B3C]">
                      {w}{" "}
                    </span>
                  ) : (
                    <span key={j}>{w} </span>
                  )
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-14 md:mt-20 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="max-w-md text-white/70 text-sm md:text-base leading-relaxed"
          >
            A modern unisex salon on Sunni Gali, Sadar — quietly obsessed with
            hair, skin, and bridal craft. Walk in as you are, leave a little
            more like yourself.
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.25 }}
            className="flex flex-wrap gap-3"
          >
            <Link to="/book" data-testid={IDS.heroBookBtn} className="btn-gold">
              <span>Book Appointment</span>
              <ArrowUpRight size={16} />
            </Link>
            <a href={BUSINESS.phoneHref} data-testid={IDS.heroCallBtn} className="btn-ghost">
              <Phone size={14} />
              <span>Call Now</span>
            </a>
          </motion.div>
        </div>

        {/* Trust strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.4 }}
          className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 border-t border-white/10 pt-8"
        >
          {[
            { k: "Unisex", v: "For everyone" },
            { k: "Salon-grade", v: "Products & care" },
            { k: "Bridal", v: "Studio & on-site" },
            { k: "Walk-ins", v: "By appointment too" },
          ].map((t, i) => (
            <div key={i} className="flex flex-col">
              <span className="eyebrow text-[#C89B3C]">{t.k}</span>
              <span className="text-white/85 mt-2 text-sm md:text-base">{t.v}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-6 right-6 md:right-10 z-10 flex items-center gap-3 text-white/60 text-xs tracking-[0.3em] uppercase [writing-mode:vertical-rl]"
      >
        <span>Scroll</span>
        <span className="w-px h-16 bg-white/30 origin-top animate-pulse" />
      </motion.div>
    </section>
  );
}

function Chapters() {
  return (
    <section className="bg-[#F3EAD9] text-[#1B1B1B] py-24 md:py-40">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <FadeUp>
              <p className="eyebrow text-[#C89B3C] mb-6">— Manifesto</p>
              <h2 className="font-display text-4xl md:text-5xl leading-[1.02]">
                A quieter kind of<br />
                <span className="italic">good-looking.</span>
              </h2>
            </FadeUp>
          </div>
          <div className="lg:col-span-8">
            <div className="grid gap-14 md:gap-16">
              {CHAPTERS.map((c, i) => (
                <FadeUp key={c.n} delay={i * 0.05}>
                  <div className="grid grid-cols-12 gap-6 md:gap-10 border-t border-black/10 pt-10 items-start">
                    <div className="col-span-12 md:col-span-4">
                      <div className="aspect-[4/5] overflow-hidden bg-black/5">
                        <img
                          src={c.img}
                          alt={c.title}
                          loading="lazy"
                          className="w-full h-full object-cover hover:scale-[1.05] transition-transform duration-[1400ms]"
                        />
                      </div>
                    </div>
                    <div className="col-span-12 md:col-span-8 md:pl-4">
                      <div className="chapter-number text-3xl md:text-4xl mb-4">
                        {c.n}
                      </div>
                      <h3 className="font-display text-2xl md:text-4xl mb-4 leading-tight">
                        {c.title}
                      </h3>
                      <p className="text-black/65 leading-relaxed max-w-xl">{c.body}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TheSpace() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [-60, 60]);
  const y2 = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section
      ref={ref}
      className="relative bg-[#1B1B1B] text-[#F3EAD9] py-24 md:py-40 overflow-hidden"
    >
      {/* Ambient number */}
      <div className="pointer-events-none select-none absolute -top-8 left-4 md:left-10 leading-none">
        <span className="font-display italic text-[22vw] md:text-[18vw] text-white/[0.04]">
          Studio
        </span>
      </div>

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10 items-start">
          {/* LEFT — Storefront */}
          <motion.div style={{ y: y1 }} className="lg:col-span-6 lg:mt-8">
            <FadeUp>
              <div className="flex items-baseline justify-between mb-4">
                <span className="chapter-number text-2xl">01</span>
                <span className="eyebrow text-white/50">The storefront</span>
              </div>
              <div className="overflow-hidden aspect-[4/5] bg-black/30 border border-white/10">
                <img
                  src={IMAGES.salonExterior}
                  alt="Good Looks salon storefront in Sunni Gali, Mansa"
                  loading="lazy"
                  className="w-full h-full object-cover hover:scale-[1.04] transition-transform duration-[1600ms] ease-[cubic-bezier(0.19,1,0.22,1)]"
                />
              </div>
              <p className="mt-5 text-white/60 text-sm max-w-sm leading-relaxed">
                Wood-slat façade, warm signage, L'Oréal Paris & Schwarzkopf
                Professional partner brands — a landmark on Sunni Gali.
              </p>
            </FadeUp>
          </motion.div>

          {/* CENTER heading */}
          <div className="lg:col-span-6 lg:pl-6 flex flex-col justify-between h-full">
            <FadeUp>
              <p className="eyebrow text-[#C89B3C] mb-6">— Step inside</p>
              <h2 className="font-display leading-[0.92] text-5xl md:text-7xl lg:text-[7vw]">
                A family salon.<br />
                <span className="italic text-[#C89B3C]">A modern academy.</span>
              </h2>
              <p className="mt-8 text-white/70 max-w-md leading-relaxed">
                Two floors of design — a bold red reception, backlit product
                walls, cinema lighting and a marble runway. Built for grooming,
                built for teaching.
              </p>

              <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
                {[
                  { k: "Signage", v: "GOOD LOOKS" },
                  { k: "Since", v: "Family & Academy" },
                  { k: "Location", v: "Sunni Gali · Sadar" },
                ].map((t) => (
                  <div key={t.k}>
                    <span className="eyebrow text-white/45 block">{t.k}</span>
                    <span className="font-display italic text-[#C89B3C] text-base leading-tight">
                      {t.v}
                    </span>
                  </div>
                ))}
              </div>
            </FadeUp>

            {/* RIGHT-BOTTOM — Reception */}
            <motion.div style={{ y: y2 }} className="mt-14">
              <FadeUp delay={0.1}>
                <div className="flex items-baseline justify-between mb-4">
                  <span className="chapter-number text-2xl">02</span>
                  <span className="eyebrow text-white/50">The reception</span>
                </div>
                <div className="overflow-hidden aspect-[5/4] bg-black/30 border border-white/10">
                  <img
                    src={IMAGES.salonReception}
                    alt="Good Looks reception counter with product wall and slat ceiling"
                    loading="lazy"
                    className="w-full h-full object-cover hover:scale-[1.04] transition-transform duration-[1600ms] ease-[cubic-bezier(0.19,1,0.22,1)]"
                  />
                </div>
                <p className="mt-5 text-white/60 text-sm max-w-md leading-relaxed">
                  Backlit product shelves, patterned tile, wooden slat ceiling
                  and marble floor with LED runners — designed to feel unmistakably
                  Good Looks.
                </p>
              </FadeUp>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}


function EditorialBreak() {
  return (
    <section className="bg-[#1B1B1B] text-[#F3EAD9] py-16 md:py-24 border-y border-white/5">
      <Marquee
        items={[
          "Hair",
          "Skin",
          "Bridal",
          "Colour",
          "Cuts",
          "Care",
        ]}
        className="text-[#C89B3C]"
      />
    </section>
  );
}

function Featured() {
  return (
    <section
      data-testid={IDS.featuredSection}
      className="bg-[#F3EAD9] text-[#1B1B1B] py-24 md:py-40"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <FadeUp>
              <p className="eyebrow text-[#C89B3C] mb-4">— Featured services</p>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.98] max-w-3xl">
                A short list of the things<br />
                <span className="italic">we love making beautiful.</span>
              </h2>
            </FadeUp>
          </div>
          <FadeUp delay={0.1}>
            <Link
              to="/services"
              data-testid={IDS.viewFullPriceListBtn}
              className="btn-outline-dark"
            >
              <span>View full price list</span>
              <ArrowUpRight size={16} />
            </Link>
          </FadeUp>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {FEATURED_SERVICES.map((s, i) => (
            <FadeUp key={s.title} delay={i * 0.06}>
              <Link
                to="/services"
                data-testid={`featured-card-${i}`}
                className="svc-card block group border border-black/10 bg-white overflow-hidden hover:border-[#C89B3C] transition-colors"
              >
                <div className="aspect-[4/5] overflow-hidden bg-black/5">
                  <img
                    src={s.img}
                    alt={s.title}
                    loading="lazy"
                    className="svc-image w-full h-full object-cover"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <h3 className="font-display text-xl leading-tight">{s.title}</h3>
                    <ArrowUpRight
                      size={18}
                      className="mt-1 shrink-0 opacity-40 group-hover:opacity-100 group-hover:text-[#C89B3C] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all"
                    />
                  </div>
                  <p className="text-black/60 text-xs leading-relaxed mb-4">{s.blurb}</p>
                  <div className="flex items-center justify-between border-t border-black/10 pt-3">
                    <span className="eyebrow text-black/50">Starting from</span>
                    <span className="font-display italic text-[#C89B3C] text-lg">
                      {s.startingFrom}
                    </span>
                  </div>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function StoryStrip() {
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const tryPlay = () => v.play().catch(() => {});
    if (v.readyState >= 2) tryPlay();
    else v.addEventListener("loadeddata", tryPlay, { once: true });
    return () => v.removeEventListener("loadeddata", tryPlay);
  }, []);
  return (
    <section className="bg-[#F3EAD9] py-24 md:py-40">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-stretch">
          <FadeUp className="lg:col-span-5">
            <div className="overflow-hidden aspect-[4/5] w-full h-full bg-black/5">
              <video
                ref={videoRef}
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_salon-sunni-gali/artifacts/8sax65zj_8225477-hd_1080_1920_25fps.mp4"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                poster={IMAGES.aboutInterior}
                className="w-full h-full object-cover"
              />
            </div>
          </FadeUp>
          <div className="lg:col-span-7 lg:pl-8 flex flex-col justify-between gap-10">
            <FadeUp>
              <p className="eyebrow text-[#C89B3C] mb-6">— Inside the studio</p>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.98]">
                We're not chasing<br />
                <span className="italic">trends. We're chasing you.</span>
              </h2>
              <p className="text-black/65 mt-8 max-w-xl leading-relaxed">
                Good Looks is built around consultation — a proper conversation
                before scissors, brush or brush touch your hair. Everything else
                follows from that: the right cut, the right colour, the right
                bridal plan across sittings.
              </p>
              <div className="mt-8">
                <Link to="/about" className="gl-link inline-flex items-center gap-2 font-medium text-[#1B1B1B] text-sm">
                  Read the studio story <ArrowUpRight size={16} />
                </Link>
              </div>
            </FadeUp>

            <div className="grid grid-cols-3 gap-6 border-t border-black/10 pt-8">
              {[
                { k: "Unisex", v: "Salon" },
                { k: "Studio", v: "Bridal room" },
                { k: "Mansa", v: "Sunni Gali" },
              ].map((t, i) => (
                <FadeUp key={i} delay={0.05 * i}>
                  <div>
                    <span className="eyebrow text-black/45">{t.k}</span>
                    <p className="font-display text-2xl mt-1">{t.v}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AppointmentBlock() {
  return (
    <section className="relative bg-[#1B1B1B] text-[#F3EAD9] py-24 md:py-40 overflow-hidden">
      <div className="absolute inset-0 opacity-40">
        <img
          src={IMAGES.interior}
          alt="Salon chairs at Good Looks"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1B1B1B] via-[#1B1B1B]/85 to-[#1B1B1B]/40" />
      </div>

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <FadeUp>
            <p className="eyebrow text-[#C89B3C] mb-6">— Book appointment</p>
            <h2 className="font-display text-5xl md:text-7xl lg:text-[7rem] leading-[0.92]">
              A seat with<br />
              <span className="italic text-[#C89B3C]">your name on it.</span>
            </h2>
            <p className="mt-8 text-white/70 max-w-xl leading-relaxed">
              Pick a service, a date and a time — we'll confirm your slot over
              call or WhatsApp within working hours. Walk-ins are welcome, but
              booking guarantees your preferred stylist.
            </p>
          </FadeUp>
        </div>

        <FadeUp className="lg:col-span-5 space-y-5" delay={0.1}>
          <Link to="/book" className="btn-gold w-full justify-between !py-5">
            <span className="font-display text-lg italic !normal-case tracking-normal">Book online</span>
            <ArrowUpRight size={18} />
          </Link>
          <a href={BUSINESS.phoneHref} className="btn-ghost w-full justify-between !py-5">
            <span className="font-display text-lg italic !normal-case tracking-normal">Call {BUSINESS.phone}</span>
            <Phone size={18} />
          </a>
          <a
            href={BUSINESS.whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost w-full justify-between !py-5"
          >
            <span className="font-display text-lg italic !normal-case tracking-normal">WhatsApp us</span>
            <ArrowUpRight size={18} />
          </a>

          <div className="pt-6 border-t border-white/10 text-white/60 text-xs">
            Open every day · Confirmations within working hours
          </div>
        </FadeUp>
      </div>
    </section>
  );
}


function GalleryTeaser() {
  const shots = GALLERY.slice(0, 6);
  return (
    <section className="bg-[#1B1B1B] text-[#F3EAD9] py-24 md:py-40">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <FadeUp>
            <p className="eyebrow text-[#C89B3C] mb-4">— Gallery</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.98]">
              Looks made<br /><span className="italic">in-house.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <Link to="/gallery" className="btn-ghost">
              <span>Browse gallery</span>
              <ArrowUpRight size={16} />
            </Link>
          </FadeUp>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
          {shots.map((src, i) => (
            <FadeUp key={i} delay={i * 0.04} className={i === 1 ? "md:row-span-2 md:col-span-1" : ""}>
              <div className={`overflow-hidden ${i === 1 ? "aspect-[3/4] md:aspect-[3/5]" : "aspect-[4/5]"}`}>
                <img
                  src={src}
                  alt={`Salon work ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1400ms] ease-[cubic-bezier(0.19,1,0.22,1)]"
                />
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqTeaser() {
  return (
    <section className="bg-[#F3EAD9] text-[#1B1B1B] py-24 md:py-40">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-4">
          <FadeUp>
            <p className="eyebrow text-[#C89B3C] mb-4">— FAQ</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.02]">
              Answers, before<br /><span className="italic">you book.</span>
            </h2>
            <Link to="/faq" className="mt-8 inline-flex gl-link items-center gap-2 text-sm">
              See all questions <ArrowUpRight size={16} />
            </Link>
          </FadeUp>
        </div>
        <div className="lg:col-span-8">
          <FadeUp>
            <Accordion type="single" collapsible className="w-full" data-testid={IDS.faqAccordion}>
              {FAQS.slice(0, 4).map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-black/10"
                >
                  <AccordionTrigger className="text-left font-display text-lg md:text-2xl py-6 hover:no-underline">
                    <span className="flex items-baseline gap-4">
                      <span className="text-[#C89B3C] text-sm">0{i + 1}</span>
                      <span>{f.q}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-black/60 leading-relaxed max-w-3xl pb-6 text-[15px]">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function LocationStrip() {
  return (
    <section className="bg-[#1B1B1B] text-[#F3EAD9] py-24 md:py-32 border-t border-white/5">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <FadeUp className="lg:col-span-5">
          <p className="eyebrow text-[#C89B3C] mb-4">— Visit us</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.02]">
            Find us on<br /><span className="italic">Sunni Gali.</span>
          </h2>
          <div className="mt-8 space-y-4">
            <p className="flex items-start gap-3 text-white/80 text-sm">
              <MapPin size={16} className="mt-1 text-[#C89B3C]" />
              <span>{BUSINESS.address.line1}, {BUSINESS.address.line2}</span>
            </p>
            <p className="flex items-start gap-3 text-white/80 text-sm">
              <Phone size={16} className="mt-1 text-[#C89B3C]" />
              <span>{BUSINESS.phone}</span>
            </p>
            <div className="pt-2">
              <p className="eyebrow text-white/50 mb-3">Hours</p>
              <div className="grid grid-cols-2 gap-y-1 text-sm text-white/70 max-w-md">
                {BUSINESS.hours.map((h) => (
                  <div key={h.day} className="contents">
                    <span>{h.day}</span>
                    <span className="text-white/85">{h.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </FadeUp>

        <FadeUp className="lg:col-span-7" delay={0.1}>
          <div className="aspect-[4/3] w-full border border-white/10 overflow-hidden">
            <iframe
              title="Good Looks Salon location"
              src={BUSINESS.mapEmbed}
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(0.6) invert(0.9)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <PageHead
        title="Good Looks Unisex Salon — Mansa, Punjab | Hair, Skin & Bridal"
        description="Good Looks is a modern unisex salon in Sunni Gali, Sadar, Mansa. Precision haircuts, skin & hair treatments, and bridal makeup. Book your appointment."
      />
      <Hero />
      <Chapters />
      <TheSpace />
      <EditorialBreak />
      <Featured />
      <StoryStrip />
      <AppointmentBlock />
      <GalleryTeaser />
      <FaqTeaser />
      <LocationStrip />
    </>
  );
}
